/**
 * Month drill-down for the Spend dashboard's Monthly Spend chart.
 *
 * Clicking a month bar opens this dialog with the invoice lines behind the
 * bar: the "Spend Lines" grid indicator resolved for just that month (an
 * `exact_period` ad-hoc filter on Invoice Posted Date, superseding the
 * indicator's saved rolling-year window). Any active page filters
 * (supplier / part) stay applied, so the list always reconciles with the
 * bar that was clicked. Lines are served largest-first, capped at 100.
 */
import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  useIndicatorData,
  type AdhocIndicatorFilter,
} from '../report/charts/useIndicatorData';

/** The clicked month, as the chart knows it. */
export interface DrillMonth {
  /** Bucket start date from the resolver, e.g. "2025-03-01". */
  iso: string;
  /** Human label, e.g. "March 2025". */
  label: string;
  value: number;
}

interface MonthDrilldownProps {
  month: DrillMonth | null;
  onClose: () => void;
  /** The "Spend Lines" grid indicator id. */
  linesId?: number;
  /** Page-level filters (supplier / part) to keep applied inside the month. */
  baseFilters: AdhocIndicatorFilter[];
  /** "View Data For" scope (group / company / site) — same slice as the charts. */
  scopeParams?: Record<string, number | undefined>;
  formatValue: (value: number) => string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** First and last instant of the month containing `iso` (local dates). */
function monthPeriod(iso: string): { start: string; end: string } | null {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = d.getMonth();
  const lastDay = new Date(y, m + 1, 0).getDate();
  return {
    start: `${y}-${pad(m + 1)}-01T00:00:00`,
    end: `${y}-${pad(m + 1)}-${pad(lastDay)}T23:59:59`,
  };
}

function cellText(
  value: unknown,
  type: string,
  formatValue: (v: number) => string,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'decimal' || type === 'int') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : formatValue(n);
  }
  if (type === 'date' || type === 'datetime') {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return String(value);
}

export default function MonthDrilldown({
  month,
  onClose,
  linesId,
  baseFilters,
  scopeParams,
  formatValue,
}: MonthDrilldownProps) {
  const filters = useMemo<AdhocIndicatorFilter[] | undefined>(() => {
    if (!month) return undefined;
    const period = monthPeriod(month.iso);
    if (!period) return undefined;
    return [
      ...baseFilters,
      // replace: the saved rolling-year window on the same field must yield
      // to the clicked month, or drilling outside it returns nothing.
      { field_dsid: 708, field_id: 3, operator: 'exact_period', value: period, replace: true },
    ];
  }, [month, baseFilters]);

  // Hook runs unconditionally; with no month selected the id is undefined
  // and nothing is fetched.
  const open = month !== null && filters !== undefined;
  const lines = useIndicatorData(open ? linesId : undefined, filters, scopeParams);

  const columns = lines.data?.columns || [];
  const rows = lines.data?.rows || [];
  const capped = (lines.data?.total_count || 0) > rows.length;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {month?.label} — {formatValue(month?.value || 0)} invoiced
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Invoice lines posted in this month, largest first
            {capped ? ` (top ${rows.length} shown)` : ''}. Page filters stay applied.
          </p>
        </DialogHeader>

        {lines.data === null || lines.loading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            No invoice lines in this month for the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left">
                  {columns.map((col) => (
                    <th key={col.key}
                        className={`whitespace-nowrap px-2 py-2 font-semibold text-slate-600 ${
                          col.type === 'decimal' || col.type === 'int' ? 'text-right' : ''}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    {columns.map((col) => (
                      <td key={col.key}
                          className={`px-2 py-1.5 text-slate-700 ${
                            col.type === 'decimal' || col.type === 'int'
                              ? 'text-right font-medium tabular-nums'
                              : ''}`}>
                        {cellText(row[col.key], col.type, formatValue)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
